import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Download, Loader2 } from "lucide-react";
import { PrintBatchBoDto } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";

const columnHelper = createColumnHelper<PrintBatchBoDto>();

type Props = {
  onDownload: (batch: PrintBatchBoDto) => void;
  downloadingBatchId: string | null;
};

export const usePrintBatchesTableColumns = ({
  onDownload,
  downloadingBatchId,
}: Props): ColumnDef<PrintBatchBoDto>[] => {
  const { t } = useTranslation("boExpeditions");
  return useMemo(
    () => [
      columnHelper.display({
        id: "createdAt",
        header: t("printBatchesColumns.createdAt"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const createdAt = new Date(info.row.original.createdAt);
          return (
            <div className="flex w-fit flex-col items-start gap-0">
              <p className="text-base font-bold text-gray-900">
                {createdAt.toLocaleDateString("fr-FR")}
              </p>
              <p className="text-sm font-bold text-gray-600">
                {t("printBatchesColumns.atTime", {
                  time: createdAt.toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </p>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "createdBy",
        header: t("printBatchesColumns.createdBy"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const createdBy = info.row.original.createdBy;
          if (!createdBy) {
            return <div className="w-fit text-base">-</div>;
          }
          return (
            <div className="max-w-[300px] truncate text-base font-bold">
              {createdBy.firstName} {createdBy.lastName}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "expeditionsCount",
        header: t("printBatchesColumns.expeditionsCount"),
        size: "auto" as unknown as number,
        cell: (info) => (
          <div className="w-fit text-base">{info.row.original.expeditionIds.length}</div>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: "auto" as unknown as number,
        cell: (info) => {
          const batch = info.row.original;
          const isDownloading = downloadingBatchId === batch.id;

          return (
            <Button
              variant="outline"
              size="sm"
              disabled={batch.expeditionIds.length === 0 || isDownloading}
              onClick={(e) => {
                e.stopPropagation();
                onDownload(batch);
              }}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t("printBatchesColumns.reprint")}
            </Button>
          );
        },
      }),
    ],
    [t, onDownload, downloadingBatchId],
  );
};
